/**
 * 远程设备状态管理类（通用）
 * 功能：检测页面刷新与关闭事件，自动终止超时设备的远程连接
 * 核心逻辑：
 *  1. 在localStorage中存储设备连接状态
 *  2. 通过定时轮询检测设备最后活动时间
 *  3. 超过阈值视为关闭页面，自动关闭远程连接
 * 注意：多标签页场景下需确保设备ID唯一性
 */

import dayjs from 'dayjs'
import { deleteByField, deserialize } from '@/utils/tool'
import { REMOTE_TYPE, RemoteAssistService, VncDevice } from '@/views/dashboard/group/client/modals/remote-assist/remote-assist.service'
import { http } from '@/common/basis/root-injector'

// 最大刷新间隔（指定时间无响应视为页面关闭）
export const MAX_REFRESH_INTERVAL_MS = 3000
export const MAX_LENGTH = 20

export interface RemoteDeviceStoreConfig {
  storageKey: string
  onClose?: (deviceInfo: VncDevice) => void | Promise<void>
}

/**
 * 远程设备存储管理类
 */
export class RemoteDeviceService {
  storageKey: string
  private onClose?: (deviceInfo: VncDevice) => void | Promise<void>
  private checkInterval?: number

  constructor(config: RemoteDeviceStoreConfig) {
    this.storageKey = config.storageKey
    this.onClose = config.onClose
  }

  /**
   * 获取设备列表
   */
  getDeviceList(): VncDevice[] {
    return deserialize(localStorage.getItem(this.storageKey)) || []
  }

  /**
   * 设置设备列表
   */
  setDeviceList(list: VncDevice[] = []) {
    localStorage.setItem(this.storageKey, JSON.stringify(list))
  }

  /**
   * 替换或添加设备
   */
  replaceDevice(item: VncDevice) {
    const list = deleteByField(this.getDeviceList(), 'device_id', item.device_id)
    list.push(item)
    if (list.length > MAX_LENGTH) {
      list.shift()
    }
    this.setDeviceList(list)
  }

  /**
   * 移除设备
   */
  removeDevice(deviceId: string) {
    const list = deleteByField(this.getDeviceList(), 'device_id', deviceId)
    this.setDeviceList(list)
  }

  /**
   * 获取设备信息
   */
  getDevice(deviceId: string): VncDevice {
    return this.getDeviceList().find(v => v.device_id === deviceId) || {} as VncDevice
  }

  /**
   * 监听页面卸载
   */
  watchUnload() {
    this.getDeviceList() || this.setDeviceList([])
    this.checkInterval = window.setInterval(() => {
      this.checkDeviceList()
    }, 100)
  }

  /**
   * 停止监听
   */
  stopWatch() {
    if (this.checkInterval !== undefined) {
      clearInterval(this.checkInterval)
      this.checkInterval = undefined
    }
  }

  /**
   * 检查设备列表状态
   * 关闭超时设备的远程连接
   */
  private checkDeviceList() {
    const list = this.getDeviceList()
    for (let i = 0; i < list.length; i++) {
      const deviceInfo = list[i]
      // 判断是否超时（关闭页面）
      const isClose = deviceInfo.unload_timestamp &&
        (dayjs().valueOf() - deviceInfo.unload_timestamp > MAX_REFRESH_INTERVAL_MS)
      if (isClose) {
        console.log('检测到远控窗口卸载后长时间未恢复，视为彻底关闭，因此前端主动上报服务端关闭')
        this.onClose?.(deviceInfo)
        this.removeDevice(deviceInfo.device_id)
      }
    }
  }
}

/**
 * VNC 远程设备存储实例
 */
export const vncDeviceStore = new RemoteDeviceService({
  storageKey: 'vnc_remote_device_list',
  onClose: (deviceInfo) => {
    const remoteAssistService = new RemoteAssistService(http)
    const apiPromise = deviceInfo.remote_type === REMOTE_TYPE.REMOTE_LOGIN
      ? remoteAssistService.turnOffRemoteLogin.bind(remoteAssistService)
      : remoteAssistService.turnOffRemoteAssist.bind(remoteAssistService)

    return apiPromise({
      device_id: deviceInfo.device_id,
      token: deviceInfo.token,
      room_id: deviceInfo.room_id,
    }).catch((error) => {
      const ret = error.responseBody?.ret // 取消http全局错误事件
    })
  }
})

/**
 * SSH 远程设备存储实例
 */
export const sshDeviceStore = new RemoteDeviceService({
  storageKey: 'ssh_remote_device_list',
  onClose: (deviceInfo) => {
    const remoteAssistService = new RemoteAssistService(http)
    return remoteAssistService.turnOffSsh({
      device_id: deviceInfo.device_id,
      token: deviceInfo.token,
      room_id: deviceInfo.room_id,
    }).catch((error) => {
      const ret = error.responseBody?.ret // 取消http全局错误事件
    })
  }
})

